/**
 * PreciosExamen Sheet - Manage exam prices within a ListaPrecio
 * Shows a sheet with the list of PrecioExamen entries and CRUD operations.
 */

import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { parseISO, isAfter, isBefore } from "date-fns";
import {
  Plus,
  Loader2,
  Trash2,
  Pencil,
  X,
  DollarSign,
  CheckCircle,
  Clock,
  XCircle,
} from "lucide-react";

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";

import { useApiError } from "@/hooks/use-api-error";
import {
  useCreatePrecioExamen,
  useUpdatePrecioExamen,
  useDeletePrecioExamen,
  usePreciosExamen,
  type ListaPrecio,
  type PrecioExamen,
  type PrecioExamenCreateData,
} from "@/hooks/use-facturacion";
import { useBuscarExamenesTipo, type ExamenTipoListItem } from "@/hooks/use-ordenes";

interface PreciosExamenSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listaPrecio: ListaPrecio | null;
}

// Vigencia badge helper
function VigenciaBadge({ precio }: { precio: PrecioExamen }) {
  const { t } = useTranslation();
  const hoy = new Date();
  const desde = parseISO(precio.vigente_desde);
  const hasta = precio.vigente_hasta ? parseISO(precio.vigente_hasta) : null;

  if (isBefore(hoy, desde)) {
    return (
      <Badge className="bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30">
        <Clock className="mr-1 h-3 w-3" />
        {t("facturacion.listasPrecios.vigencia.future")}
      </Badge>
    );
  }
  if (hasta && isAfter(hoy, hasta)) {
    return (
      <Badge variant="secondary">
        <XCircle className="mr-1 h-3 w-3" />
        {t("facturacion.listasPrecios.vigencia.expired")}
      </Badge>
    );
  }
  return (
    <Badge className="bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30">
      <CheckCircle className="mr-1 h-3 w-3" />
      {t("facturacion.listasPrecios.vigencia.active")}
    </Badge>
  );
}

// Form schema
const precioSchema = z.object({
  examen_search: z.string().optional(),
  examen_tipo: z.number().min(1, "facturacion.listasPrecios.validation.examenRequired"),
  precio: z.string().min(1, "facturacion.listasPrecios.validation.precioRequired"),
  vigente_desde: z.string().min(1, "facturacion.listasPrecios.validation.vigentDesdeRequired"),
  vigente_hasta: z.string().optional(),
});

type PrecioFormData = z.infer<typeof precioSchema>;

export function PreciosExamenSheet({ open, onOpenChange, listaPrecio }: PreciosExamenSheetProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();

  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingPrecio, setEditingPrecio] = useState<PrecioExamen | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PrecioExamen | null>(null);
  const [examenSearch, setExamenSearch] = useState("");
  const [selectedExamenName, setSelectedExamenName] = useState("");

  // Examen search via hook
  const { data: examenSearchResults, isFetching: isSearching } =
    useBuscarExamenesTipo(examenSearch);

  // Fetch precios for this lista
  const {
    data: preciosData,
    isLoading: isLoadingPrecios,
    refetch: refetchPrecios,
  } = usePreciosExamen(listaPrecio?.id, { enabled: open });

  const precios = preciosData?.results || [];

  // Mutations
  const createMutation = useCreatePrecioExamen({
    onSuccess: () => {
      toast.success(t("facturacion.listasPrecios.precios.toast.created"));
      refetchPrecios();
      resetForm();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const updateMutation = useUpdatePrecioExamen({
    onSuccess: () => {
      toast.success(t("facturacion.listasPrecios.precios.toast.updated"));
      refetchPrecios();
      resetForm();
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const deleteMutation = useDeletePrecioExamen({
    onSuccess: () => {
      toast.error(t("facturacion.listasPrecios.precios.toast.deleted"));
      refetchPrecios();
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(getErrorMessage(err)),
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<PrecioFormData>({
    resolver: zodResolver(precioSchema),
    defaultValues: {
      examen_search: "",
      examen_tipo: 0,
      precio: "",
      vigente_desde: new Date().toISOString().split("T")[0],
      vigente_hasta: "",
    },
  });

  const resetForm = useCallback(() => {
    setIsFormVisible(false);
    setEditingPrecio(null);
    setExamenSearch("");
    setSelectedExamenName("");
    reset({
      examen_search: "",
      examen_tipo: 0,
      precio: "",
      vigente_desde: new Date().toISOString().split("T")[0],
      vigente_hasta: "",
    });
  }, [reset]);

  const handleAddNew = () => {
    resetForm();
    setIsFormVisible(true);
  };

  const handleEdit = (precio: PrecioExamen) => {
    setEditingPrecio(precio);
    setSelectedExamenName(precio.examen_nombre);
    setValue("examen_tipo", precio.examen_tipo);
    setValue("precio", precio.precio);
    setValue("vigente_desde", precio.vigente_desde);
    setValue("vigente_hasta", precio.vigente_hasta || "");
    setIsFormVisible(true);
  };

  // Examen search
  const handleExamenSearch = useCallback((query: string) => {
    setExamenSearch(query);
  }, []);

  const handleSelectExamen = (examen: ExamenTipoListItem) => {
    setValue("examen_tipo", examen.id, { shouldValidate: true });
    setSelectedExamenName(`${examen.codigo} - ${examen.nombre}`);
    setExamenSearch("");
  };

  const onSubmit = (data: PrecioFormData) => {
    if (!listaPrecio) return;
    const payload: PrecioExamenCreateData = {
      lista: listaPrecio.id,
      examen_tipo: data.examen_tipo,
      precio: parseFloat(data.precio),
      vigente_desde: data.vigente_desde,
      vigente_hasta: data.vigente_hasta || null,
    };

    if (editingPrecio) {
      updateMutation.mutate({ id: editingPrecio.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-xl flex flex-col">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              {t("facturacion.listasPrecios.precios.title")}
            </SheetTitle>
            <SheetDescription>
              {listaPrecio?.nombre} — {t(`facturacion.listasPrecios.tipos.${listaPrecio?.tipo}`)}
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 flex flex-col gap-4 mt-4 min-h-0">
            {/* Add button */}
            {!isFormVisible && (
              <Button onClick={handleAddNew} size="sm" className="self-start">
                <Plus className="mr-2 h-4 w-4" />
                {t("facturacion.listasPrecios.precios.addPrice")}
              </Button>
            )}

            {/* Form */}
            {isFormVisible && (
              <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">
                    {editingPrecio
                      ? t("facturacion.listasPrecios.precios.editPrice")
                      : t("facturacion.listasPrecios.precios.addPrice")}
                  </h4>
                  <Button variant="ghost" size="icon" onClick={resetForm}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                  {/* Examen selector */}
                  <div className="space-y-2">
                    <Label>{t("facturacion.listasPrecios.precios.fields.examen")} *</Label>
                    {selectedExamenName && !editingPrecio ? (
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-sm py-1">
                          {selectedExamenName}
                        </Badge>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => {
                            setSelectedExamenName("");
                            setValue("examen_tipo", 0);
                          }}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ) : editingPrecio ? (
                      <Badge variant="outline" className="text-sm py-1">
                        {selectedExamenName}
                      </Badge>
                    ) : (
                      <div className="relative">
                        <Input
                          placeholder={t("facturacion.listasPrecios.precios.fields.searchExamen")}
                          value={examenSearch}
                          onChange={(e) => handleExamenSearch(e.target.value)}
                        />
                        {((examenSearchResults && examenSearchResults.length > 0) ||
                          isSearching) && (
                          <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
                            {isSearching ? (
                              <div className="p-3 text-sm text-muted-foreground text-center">
                                <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                                {t("common.searching")}
                              </div>
                            ) : (
                              examenSearchResults?.map((ex) => (
                                <button
                                  key={ex.id}
                                  type="button"
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                                  onClick={() => handleSelectExamen(ex)}
                                >
                                  <span className="font-mono text-xs text-muted-foreground">
                                    {ex.codigo}
                                  </span>{" "}
                                  — {ex.nombre}
                                </button>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {errors.examen_tipo && (
                      <p className="text-sm text-destructive">
                        {t(errors.examen_tipo.message as string)}
                      </p>
                    )}
                  </div>

                  {/* Precio */}
                  <div className="space-y-2">
                    <Label>{t("facturacion.listasPrecios.precios.fields.precio")} *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      {...register("precio")}
                    />
                    {errors.precio && (
                      <p className="text-sm text-destructive">
                        {t(errors.precio.message as string)}
                      </p>
                    )}
                  </div>

                  {/* Vigencia */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{t("facturacion.listasPrecios.precios.fields.vigente_desde")} *</Label>
                      <Input type="date" {...register("vigente_desde")} />
                      {errors.vigente_desde && (
                        <p className="text-sm text-destructive">
                          {t(errors.vigente_desde.message as string)}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>{t("facturacion.listasPrecios.precios.fields.vigente_hasta")}</Label>
                      <Input type="date" {...register("vigente_hasta")} />
                    </div>
                  </div>

                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" size="sm" onClick={resetForm}>
                      {t("common.cancel")}
                    </Button>
                    <Button type="submit" size="sm" disabled={isSubmitting}>
                      {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      {editingPrecio ? t("common.save") : t("common.add")}
                    </Button>
                  </div>
                </form>
              </div>
            )}

            <Separator />

            {/* Precios list */}
            <ScrollArea className="flex-1">
              {isLoadingPrecios ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : precios.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-sm">
                  {t("facturacion.listasPrecios.precios.empty")}
                </div>
              ) : (
                <div className="space-y-2">
                  {precios.map((precio) => (
                    <div
                      key={precio.id}
                      className="flex items-center justify-between gap-3 p-3 border rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{precio.examen_nombre}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-sm font-semibold text-primary">
                            L. {precio.precio}
                          </span>
                          <VigenciaBadge precio={precio} />
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {precio.vigente_desde}
                          {precio.vigente_hasta ? ` → ${precio.vigente_hasta}` : ` → ∞`}
                        </p>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleEdit(precio)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setDeleteTarget(precio)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t("facturacion.listasPrecios.precios.confirmDelete")}
        description={t("facturacion.listasPrecios.precios.confirmDeleteDesc", {
          examen: deleteTarget?.examen_nombre,
        })}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </>
  );
}
